import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { api, assetUrl } from "@/lib/api";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import Marquee from "@/components/landing/Marquee";
import About from "@/components/landing/About";
import Apps from "@/components/landing/Apps";
import Contact from "@/components/landing/Contact";
import Footer from "@/components/landing/Footer";
import AnnouncementPopup from "@/components/landing/AnnouncementPopup";
import PhotoSubmissionModal from "@/components/landing/PhotoSubmissionModal";

const setMetaTag = (name, value) => {
  if (!value) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const setHeadLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const injectGA = (id) => {
  if (!id || window.__ktnGaLoaded) return;
  window.__ktnGaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", id);
};

const injectMetaPixel = (id) => {
  if (!id || window.__ktnFbqLoaded) return;
  window.__ktnFbqLoaded = true;
  const n = (window.fbq = window.fbq || function fbq() { n.queue.push(arguments); });
  if (!window._fbq) window._fbq = n;
  n.queue = n.queue || [];
  n.loaded = true;
  n.version = "2.0";
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(s);
  window.fbq("init", id);
  window.fbq("track", "PageView");
};

export default function Landing() {
  const [content, setContent] = useState(null);
  const [error, setError] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    api
      .get("/content")
      .then((r) => setContent(r.data))
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    if (!content) return;
    const { seo = {}, branding = {}, analytics = {} } = content;
    if (seo.title) document.title = seo.title;
    setMetaTag("description", seo.description);
    setMetaTag("keywords", seo.keywords);
    if (branding.favicon) setHeadLink("icon", assetUrl(branding.favicon));
    if (branding.app_icon) setHeadLink("apple-touch-icon", assetUrl(branding.app_icon));
    injectGA(analytics.ga_id);
    injectMetaPixel(analytics.meta_pixel_id);
  }, [content]);

  const scrollTo = (target) => {
    lenisRef.current?.scrollTo(target, { offset: -64, duration: 1.5 });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-charcoal flex flex-col items-center justify-center gap-4" data-testid="landing-error">
        <img src="/logo.png" alt="Kannur TheyyamNow" className="w-24 h-auto" />
        <p className="text-ivory/70 text-sm tracking-[0.2em] uppercase">Unable to load — please refresh</p>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-charcoal flex flex-col items-center justify-center gap-6" data-testid="landing-loader">
        <motion.img
          src="/logo.png"
          alt="Kannur TheyyamNow"
          className="w-28 h-auto"
          animate={{ scale: [1, 1.06, 1], opacity: [0.75, 1, 0.75] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
        />
        <p className="text-ivory/60 text-xs uppercase tracking-[0.4em]">Kannur TheyyamNow</p>
      </div>
    );
  }

  return (
    <div className="bg-ivory text-charcoal font-sans" data-testid="landing-page">
      <div className="grain" aria-hidden="true" />
      <Header onNavigate={scrollTo} />
      <main>
        <Hero banners={content.banners || []} onNavigate={scrollTo} />
        <Marquee />
        <About about={content.about || {}} />
        <Apps apps={content.apps || {}} />
        <Contact />
      </main>
      <Footer content={content} onNavigate={scrollTo} />
      <AnnouncementPopup announcement={content.announcement} onOpenPhotoForm={() => setPhotoOpen(true)} onNavigate={scrollTo} />
      <PhotoSubmissionModal open={photoOpen} onClose={() => setPhotoOpen(false)} />
    </div>
  );
}
